"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";

interface Props {
  // Legacy escape hatch (layout still passes the middleware-stamped path).
  // usePathname() is preferred internally — it stays correct on client-side
  // navigations where the server-rendered prop would go stale.
  currentPath?: string;
}

interface Section {
  label: string;
  emoji: string;
  href: string;
  matchPrefix: string;
}

const SECTIONS: Section[] = [
  { label: "Сегодня", emoji: "🎯", href: "/today", matchPrefix: "/today" },
  { label: "Чат", emoji: "💬", href: "/chat", matchPrefix: "/chat" },
  { label: "Конспект", emoji: "📒", href: "/notebook", matchPrefix: "/notebook" },
  { label: "Уроки", emoji: "📚", href: "/lessons", matchPrefix: "/lessons" },
  { label: "Тренажёры", emoji: "🏋️", href: "/exercises", matchPrefix: "/exercises" },
  { label: "Лексика", emoji: "🗂️", href: "/lexico", matchPrefix: "/lexico" },
  { label: "Мои слова", emoji: "📖", href: "/vocab", matchPrefix: "/vocab" },
  { label: "Предлоги", emoji: "🔤", href: "/prepositions", matchPrefix: "/prepositions" },
  { label: "Прогресс", emoji: "📊", href: "/dashboard", matchPrefix: "/dashboard" },
  { label: "Дуэль", emoji: "⚔️", href: "/duel", matchPrefix: "/duel" },
  {
    label: "Настройки",
    emoji: "⚙️",
    href: "/settings",
    matchPrefix: "/settings",
  },
];

export function SectionNav({ currentPath }: Props) {
  const pathname = usePathname() ?? currentPath ?? "/";
  return (
    <nav
      aria-label="Разделы"
      className="
        flex min-w-0 max-w-full flex-row items-center justify-start gap-1
        overflow-x-auto overscroll-x-contain xl:justify-center sm:gap-2
        border-b border-paper-edge bg-paper-2 px-2 py-2
      "
    >
      {SECTIONS.map((s) => {
        const active = pathname === s.matchPrefix || pathname.startsWith(`${s.matchPrefix}/`);
        return (
          <Link
            key={s.href}
            href={s.href}
            aria-current={active ? "page" : undefined}
            aria-label={s.label}
            title={s.label}
            className={`
              flex min-h-11 shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md px-3 py-2 text-sm
              hover:bg-paper-edge/50 focus-visible:outline-2 focus-visible:outline-terracotta
              ${active ? "bg-paper-edge/80 font-medium text-ink" : "text-ink-2"}
            `}
          >
            <span aria-hidden>{s.emoji}</span>
            <span>{s.label}</span>
          </Link>
        );
      })}
    </nav>
  );
}
